/**
 * Model definitions.
 *
 * A model here is a *definition*, not an entitlement: the fact that a model
 * exists says nothing about whether any account can reach it. That question is
 * answered by the Account × Model matrix on the accounts screen, and this page
 * links there rather than implying an answer it does not have.
 *
 * Everything on a row is either a fact the provider published (context window,
 * capability flags) or a fact Kimene observed (when it was last discovered).
 * Where the provider published nothing, the cell says so in the UNKNOWN tone
 * instead of showing a plausible default.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Page, PageHeader, Panel, Badge, Meter, Button, type Tone } from '@/components/primitives'
import { QueryState, Toolbar, SelectField, TextField } from '@/components/controls'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import type { ISOTime, Meta, Provider } from '@/types/domain'

/**
 * api.PageResponse. Declared here rather than imported from the API client,
 * whose copy has `nextCursor` required — the server omits it on the last page,
 * which is the only page this catalogue currently has.
 */
interface Page<T> {
  items: T[]
  nextCursor?: string
  hasMore: boolean
}

/**
 * domain.ModelDefinition, exactly as `/models` sends it.
 *
 * `/models` serves the domain record whole rather than a response DTO, so the
 * envelope fields come with it and every optional here is an `omitempty` on the
 * Go struct. Declared against the handler rather than reused from types/domain,
 * whose copy predates `maxOutputTokens`, `supportedEfforts` and
 * `discoverySource` — the last of which this file had been re-adding by hand as
 * an intersection type, which is a sign the underlying mirror was incomplete
 * rather than a reason to patch it at the call site.
 */
interface ModelDefinition extends Meta {
  provider: Provider
  externalId: string
  displayName: string
  family: string
  status: 'ACTIVE' | 'DEPRECATED' | 'RETIRED' | 'PREVIEW'
  contextWindowTokens?: number
  maxOutputTokens?: number
  supportedEfforts?: string[]
  supportsTools: boolean
  supportsVision: boolean
  supportsStructuredOutput: boolean
  supportsLongContext: boolean
  codeCapability: number
  lastDiscoveredAt?: ISOTime
  discoverySource?: string
}

const ALL = '*'

function modelStatusTone(status: ModelDefinition['status']): Tone {
  switch (status) {
    case 'ACTIVE':
      return 'ok'
    case 'PREVIEW':
      return 'info'
    case 'DEPRECATED':
      return 'warn'
    case 'RETIRED':
      return 'neutral'
  }
}

/** Context windows are quoted in tokens and run to seven digits; a raw integer
 *  is unreadable at a glance in a dense table. */
function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000)
    return `${(tokens / 1_000_000).toFixed(tokens % 1_000_000 === 0 ? 0 : 1)}M`
  if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`
  return String(tokens)
}

export function ModelsPage() {
  const t = useT()
  const { locale } = useLocale()

  const [search, setSearch] = useState('')
  const [provider, setProvider] = useState<string>(ALL)
  const [family, setFamily] = useState<string>(ALL)
  const [status, setStatus] = useState<string>(ALL)

  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => api.get<Page<ModelDefinition>>('/models'),
  })

  const items = useMemo(() => models.data?.items ?? [], [models.data])

  const families = useMemo(
    () => Array.from(new Set(items.map((m) => m.family).filter(Boolean))).sort(),
    [items],
  )
  const providers = useMemo(() => Array.from(new Set(items.map((m) => m.provider))).sort(), [items])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return items.filter((m) => {
      if (provider !== ALL && m.provider !== provider) return false
      if (family !== ALL && m.family !== family) return false
      if (status !== ALL && m.status !== status) return false
      if (!needle) return true
      return (
        m.displayName.toLowerCase().includes(needle) || m.externalId.toLowerCase().includes(needle)
      )
    })
  }, [items, search, provider, family, status])

  const filtersApplied =
    provider !== ALL || family !== ALL || status !== ALL || search.trim() !== ''

  return (
    <Page
      header={
        <PageHeader
          title={t('model.title')}
          subtitle={t('common.count', { count: filtered.length })}
          actions={
            <Link to="/accounts">
              <Button size="sm">{t('account.matrix')} →</Button>
            </Link>
          }
        >
          <Toolbar
            actions={
              filtersApplied ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearch('')
                    setProvider(ALL)
                    setFamily(ALL)
                    setStatus(ALL)
                  }}
                >
                  {t('common.clearFilters')}
                </Button>
              ) : undefined
            }
          >
            <TextField
              label={t('topbar.search')}
              type="search"
              value={search}
              onChange={setSearch}
              placeholder={t('common.searchPlaceholder')}
            />
            <SelectField
              label={t('account.provider')}
              value={provider}
              onChange={setProvider}
              options={[
                { value: ALL, label: t('common.all') },
                ...providers.map((p) => ({ value: p, label: p })),
              ]}
            />
            <SelectField
              label={t('model.family')}
              value={family}
              onChange={setFamily}
              options={[
                { value: ALL, label: t('common.all') },
                ...families.map((f) => ({ value: f, label: f })),
              ]}
            />
            <SelectField
              label={t('model.status')}
              value={status}
              onChange={setStatus}
              options={[
                { value: ALL, label: t('common.all') },
                { value: 'ACTIVE', label: 'ACTIVE' },
                { value: 'PREVIEW', label: 'PREVIEW' },
                { value: 'DEPRECATED', label: 'DEPRECATED' },
                { value: 'RETIRED', label: 'RETIRED' },
              ]}
            />
          </Toolbar>
        </PageHeader>
      }
    >
      <Panel flush>
        <QueryState
          pending={models.isPending}
          error={models.error}
          onRetry={() => void models.refetch()}
          empty={filtered.length === 0}
          emptyTitle={items.length === 0 ? t('model.empty') : t('common.noMatches')}
          emptyHint={items.length === 0 ? t('model.emptyHint') : t('common.noMatchesHint')}
        >
          <div className="k-tablewrap">
            <table className="k-table k-table--responsive">
              <caption className="k-visually-hidden">{t('model.title')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('common.name')}</th>
                  <th scope="col">{t('model.family')}</th>
                  <th scope="col">{t('account.provider')}</th>
                  <th scope="col">{t('model.status')}</th>
                  <th scope="col">{t('model.context')}</th>
                  <th scope="col">{t('model.capabilities')}</th>
                  <th scope="col">{t('model.codeCapability')}</th>
                  <th scope="col">{t('model.discoverySource')}</th>
                  <th scope="col">{t('model.lastDiscovered')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id}>
                    <td className="k-td--strong" data-label={t('common.name')}>
                      <div>
                        {m.displayName}
                        <span className="k-sub k-mono">{m.externalId}</span>
                      </div>
                    </td>
                    <td data-label={t('model.family')}>
                      {m.family || <span className="k-dash">—</span>}
                    </td>
                    <td data-label={t('account.provider')}>
                      <Badge tone="neutral" subtle>
                        {m.provider}
                      </Badge>
                    </td>
                    <td data-label={t('model.status')}>
                      <Badge tone={modelStatusTone(m.status)} subtle>
                        {m.status}
                      </Badge>
                    </td>
                    <td className="k-mono k-td--num" data-label={t('model.context')}>
                      {m.contextWindowTokens != null ? (
                        <span
                          title={t('model.tokens', {
                            count: m.contextWindowTokens,
                          })}
                        >
                          {formatTokens(m.contextWindowTokens)}
                        </span>
                      ) : (
                        <Badge tone="unknown" subtle title={t('model.contextUnknown')}>
                          {t('state.unknown')}
                        </Badge>
                      )}
                      {/* Output ceiling and context window are different limits
                          and the provider publishes them separately. Shown only
                          when it did: an absent ceiling is not an unlimited one. */}
                      {m.maxOutputTokens != null && (
                        <span className="k-sub">
                          {t('model.maxOutput')} {formatTokens(m.maxOutputTokens)}
                        </span>
                      )}
                    </td>
                    <td data-label={t('model.capabilities')}>
                      <CapabilityFlags model={m} />
                    </td>
                    <td data-label={t('model.codeCapability')}>
                      <div className="k-meterpair">
                        <Meter
                          value={m.codeCapability}
                          tone="accent"
                          ariaLabel={`${m.displayName} ${t('model.codeCapability')}`}
                        />
                        <span className="k-mono">{m.codeCapability.toFixed(2)}</span>
                      </div>
                    </td>
                    <td className="k-mono" data-label={t('model.discoverySource')}>
                      {m.discoverySource || <span className="k-dash">—</span>}
                    </td>
                    <td data-label={t('model.lastDiscovered')}>
                      {m.lastDiscoveredAt ? (
                        formatRelative(m.lastDiscoveredAt, locale)
                      ) : (
                        <Badge tone="unknown" subtle>
                          {t('state.never')}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </Panel>
    </Page>
  )
}

/** Capability flags are shown only when true. A row of five badges where three
 *  say "no" is harder to scan than a row of two that say "yes". */
function CapabilityFlags({ model }: { model: ModelDefinition }) {
  const t = useT()
  const flags: Array<[boolean, string]> = [
    [model.supportsTools, t('model.cap.tools')],
    [model.supportsVision, t('model.cap.vision')],
    [model.supportsStructuredOutput, t('model.cap.structured')],
    [model.supportsLongContext, t('model.cap.longContext')],
  ]
  const on = flags.filter(([v]) => v)
  if (on.length === 0) return <span className="k-dash">—</span>
  return (
    <span className="k-chips">
      {on.map(([, label]) => (
        <Badge key={label} tone="accent" subtle>
          {label}
        </Badge>
      ))}
    </span>
  )
}
